import Vue from 'vue'
import Vuex from 'vuex'
import createLogger from 'vuex/dist/logger'
import Types from './types.js'
import _ from 'lodash'
// import queryString from 'query-string'

Vue.use(Vuex)

const db = window.firebase.firestore()
const debug = process.env.NODE_ENV !== 'production'

// const userRef = db.collection('user')
const businessRef = db.collection('business')
const partnershipRef = db.collection('partnership')

const monthStart = new Date()
monthStart.setDate(1)
monthStart.setHours(0, 0, 0, 0)

// hold open query listeners so they can be removed if needed
let queryListeners = {}
window.queryListeners = queryListeners

function dateToLocalIsoString(date) {
  return date.getFullYear() +
    '-' + _.padStart(date.getMonth() + 1, 2, '0') +
    '-' + _.padStart(date.getDate(), 2, '0')
}

function formatMoney(cents) {
  const str = _.padStart(cents + '', 3, '0')
  const dollars = str.substr(0, str.length - 2)
  const pennies = str.substr(-2)
  return `$${dollars}.${pennies}`
}

/**
 * Attaches Firestore listener to actions, letting us receive data from Firestore
 * - automatically clears any old listener bound to a given action with the same id
 * - idempotent if action, id, query are equal
 * @param {string} action - the action to invoke on the store when a db update is received
 * @param {string} id - identifier for the listener bound to this action. Same ID as previous will cause deregistering and replacement of listener
 * @param {CollectionReference or DocumentReference} query - firestore ref on which to listen. If falsy, then any old listener can still be deregistered, and action is called with clearing data
 * @param {object} clearingData - if provided, action is immediately invoked with clearing data to set loading/empty state and clear old data
 * @param {function} transform - modifies snapshot from db for action
 * @param {string} cachingKey - key to cache transformed snapshot in localstorage; also removes realtime listening
 */
function bindListener({ action, id = '_default', query, clearingData, transform, cachingKey }) {
  // get the previous listener if there was one and dedupe if action + id + query are identical
  queryListeners[action] = queryListeners[action] || {}
  if (queryListeners[action][id] && query && queryListeners[action][id].query === query.ot.toString()) {
    console.log('deduped query', query.ot.toString())
    return
  }

  // deregister old firestore listener to prevent any more updates
  queryListeners[action][id] && queryListeners[action][id].remove && queryListeners[action][id].remove()
  delete queryListeners[action][id]

  // if query data is in cache, then call action with it and skip clearing or realtime updates
  const cachedResult = cachingKey && JSON.parse(localStorage.getItem('_rb-data-cache-' + cachingKey) || null)
  if (cachedResult) {
    store.dispatch(action, { ...cachedResult, cached: true })
    return
  }

  // clear old data by setting empty/intermediate/loading data, which will propogate clearing for dependent listeners
  clearingData && store.dispatch(action, clearingData)

  // if there's no new query, then just keep it cleared and do not bind a new listener; this allows using ternary operator for conditional clearing
  if (!query) return

  // make new listener that calls this action
  if (cachingKey) {
    query.get().then(snapshot => {
      const result = transform(snapshot)
      localStorage.setItem('_rb-data-cache-' + cachingKey, JSON.stringify(result))
      store.dispatch(action, result)
    })
  } else {
    const remove = query.onSnapshot(snapshot => {
      const result = transform(snapshot)
      store.dispatch(action, result)
    })
    queryListeners[action][id] = {
      remove,
      query: query.ot.toString()
    }
  }
}

const store = new Vuex.Store({
  state: {
    user: Types.loadingUser,
    currentBusinessId: null,
    businessList: {}, // id indexed businesses that this user has access to; always updates in realtime
    isBusinessListLoading: false,
    partnershipList: {}, // id indexed partnership objects for currently selected business
    selectedMonth: monthStart,
    currentBusinessViews: { byDate: {} }, // date indexed aggregated count of views
    isCurrentBusinessViewsLoading: false,
    currentBusinessPurchases: {}, // date indexed aggregation of count of orders, purchase subtotal, and affiliate commission
    referredPartnerViews: { byPartner: {} }, // partnership indexed then date indexed aggregated count of views referred
    referredPartnerPurchases: { byPartner: {} } // partnership indexed then date indexed aggregation of count of orders, purchase subtotal, and affiliate commission
  },
  getters: {
    user: state => state.user,
    isLoadingUser: state => state.user === Types.loadingUser,
    businessList: state => state.businessList,
    currentBusiness: state => (state.currentBusinessId && state.businessList[state.currentBusinessId]) || {},
    currentBusinessId: state => state.currentBusinessId,
    isBusinessListLoading: state => state.isBusinessListLoading,
    isBusinessListEmpty: state => !state.isBusinessListLoading && _.isEmpty(state.businessList),
    businessListLength: state => _.size(state.businessList),
    currentBusinessViews: state => _.sum(_.values(state.currentBusinessViews.byDate)),
    referredPartnerViews: state => _.sum(_.values(state.referredPartnerViews.byPartner)),
    referredPurchases: state => _.sumBy(_.values(state.referredPartnerPurchases.byPartner), 'totalCount'),
    estimatedEarnings: state => formatMoney(_.sumBy(_.values(state.referredPartnerPurchases.byPartner), 'totalCommission'))
  },
  mutations: {
    setUser(state, { user }) {
      state.user = user
    },
    setCurrentBusinessId(state, { id }) {
      state.currentBusinessId = id
    },
    setBusinessList(state, { list }) {
      state.businessList = list
    },
    setIsBusinessListLoading(state, { isBusinessListLoading }) {
      state.isBusinessListLoading = isBusinessListLoading
    },
    setPartnershipList(state, { list }) {
      state.partnershipList = list
    },
    setCurrentBusinessViews(state, { views, dateKey }) {
      if (dateKey) {
        const nextViews = { ...state.currentBusinessViews.byDate }
        nextViews[dateKey] = views
        state.currentBusinessViews.byDate = nextViews
      }
    },
    setReferredPartnerViews(state, { partnershipId, partnerViews }) {
      const nextViews = { ...state.referredPartnerViews.byPartner }
      if (partnerViews) {
        nextViews[partnershipId] = partnerViews
      } else {
        delete nextViews[partnershipId]
      }
      state.referredPartnerViews.byPartner = nextViews
    },
    setReferredPartnerPurchasees(state, { partnershipId, totalCount, totalCommission }) {
      const nextPurchases = { ...state.referredPartnerPurchases.byPartner }
      if (totalCount) {
        nextPurchases[partnershipId] = { totalCount, totalCommission }
      } else {
        delete nextPurchases[partnershipId]
      }
      state.referredPartnerPurchases.byPartner = nextPurchases
    }
  },
  actions: {
    setUser({ dispatch, commit, state }, { user }) {
      commit('setUser', { user })
      if (user == null) {
        localStorage.removeItem('currentBusinessId')
        localStorage.setItem('viewCache', {})
      }

      // TODO get the user data object

      // bind business listener
      bindListener({
        action: 'setBusinessList',
        query: user && user.uid ? businessRef.where(`users.${user.uid}`, '==', 'full') : null,
        clearingData: { list: {}, loading: true },
        transform: snapshot => ({
          list: _.keyBy(_.map(snapshot.docs, item => ({ ...item.data(), id: item.id })), 'id'),
          loading: false
        })
      })
    },
    setBusinessList({ commit, state, dispatch }, { list, loading }) {
      commit('setBusinessList', { list })
      commit('setIsBusinessListLoading', { isBusinessListLoading: loading })

      // if no business in the list is selected, then dispatch selecting one automatically
      // this handles app startup and business deletions
      let nextId = state.currentBusinessId
      if (!state.currentBusinessId || !state.businessList[state.currentBusinessId]) {
        // TODO if the business was just selected and they were removed, then we might want to alert the user about what happened
        const storedId = localStorage.getItem('currentBusinessId')
        nextId = storedId && state.businessList[storedId]
          ? storedId
          : _.keys(state.businessList)[0]
        dispatch('setCurrentBusinessId', { id: nextId })
      }

      if (!loading && window.location.search) {
        // at this point, we have the businesses and user; if we came from stripe's redirect then try linking the payment
        // const parsedQuery = queryString.parse(window.location.search)
        if (window.location.search.indexOf('scope') && !state.businessList[nextId].paymentMethod) {
          console.log('connecting stripe')
          window.db.collection('business').doc(nextId).set({
            paymentMethod: window.location.search
          }, { merge: true })
        }
      }
    },
    setCurrentBusinessId({ commit, state, dispatch }, { id }) {
      commit('setCurrentBusinessId', { id })
      if (id) localStorage.setItem('currentBusinessId', id)

      // whenever a new business is selected, update partnerships listener
      bindListener({
        action: 'setPartnershipList',
        query: id ? partnershipRef.where('affiliate', '==', businessRef.doc(id)) : null,
        clearingData: !_.isEmpty(state.partnershipList) && { list: {} },
        transform: snapshot => ({
          list: _.keyBy(_.map(snapshot.docs, item => ({
            ...item.data(),
            merchant: item.data().merchant.id,
            affiliate: item.data().affiliate.id,
            id: item.id
          })), 'id')
        })
      })

      dispatch('updateMetrics')
    },
    setPartnershipList({ commit, state }, { list }) {
      const partnershipsToBind = _.defaults({}, list, state.partnershipList)
      commit('setPartnershipList', { list })

      // add, update and remove listeners for partnership events
      _.forEach(partnershipsToBind, partnership => {
        // views
        bindListener({
          action: 'setReferredPartnerViews',
          id: partnership.id,
          query: list[partnership.id] // bind null if partnership was removed
            ? businessRef.doc(partnership.merchant).collection('viewEvent').where('affiliateCredit', '==', partnership.id)
            : null,
          clearingData: { partnershipId: partnership.id, partnerViews: null },
          transform: snapshot => ({ partnershipId: partnership.id, partnerViews: _.size(_.uniqBy(_.invokeMap(snapshot.docs, 'data'), 'uuid')) })
        })
        // purchases
        bindListener({
          action: 'setReferredPartnerPurchasees',
          id: partnership.id,
          query: list[partnership.id] // bind null if partnership was removed
            ? businessRef.doc(partnership.merchant).collection('purchaseEvent').where('affiliateCredit', '==', partnership.id)
            : null,
          clearingData: { partnershipId: partnership.id, totalCount: null, totalCommission: null },
          transform: snapshot => ({ partnershipId: partnership.id, totalCount: snapshot.size, totalCommission: _.sumBy(_.invokeMap(snapshot.docs, 'data'), 'affiliateCommissionCents') })
        })
      })
    },
    updateMetrics({ commit, state }) {
      // updates all data and listeners for metrics
      // Go through all days before yesterday in current month and set them for view metrics; will fetch if we do not have the metrics stored; re-key cache if data structure changes
      const now = new Date()
      const endDate = state.selectedMonth.getMonth() === now.getMonth() && state.selectedMonth.getFullYear() === now.getFullYear()
          ? now.getDate() : (() => { now.setMonth(state.selectedMonth.getMonth() + 1); return now })()
      _.range(1, endDate).forEach(date => {
        // bind a listener that sets values for that day, include cache key so that the result is stored
        // TODO make getter sum all things
        const startTime = new Date(state.selectedMonth)
        startTime.setHours(0, 0, 0, 0)
        startTime.setDate(date)
        const endTime = new Date(startTime)
        endTime.setDate(startTime.getDate() + 1)
        const dateStr = dateToLocalIsoString(startTime)
        bindListener({
          action: 'setCurrentBusinessViews',
          id: dateStr,
          query: state.currentBusinessId
            ? businessRef.doc(state.currentBusinessId).collection('viewEvent')
                .where('timestamp', '>=', startTime)
                .where('timestamp', '<', endTime).orderBy('timestamp')
            : null,
          clearingData: { views: 0, dateKey: dateStr },
          transform: snapshot => ({ views: _.size(_.uniqBy(_.invokeMap(snapshot.docs, 'data'), 'uuid')), dateKey: dateStr }),
          cachingKey: 'viewsRev3-' + state.currentBusinessId + dateStr
        })
      })

      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)
      bindListener({
        action: 'setCurrentBusinessViews',
        // TODO also ensure currentMonth is this one, otherwise we need no listener
        query: state.currentBusinessId ? businessRef.doc(state.currentBusinessId).collection('viewEvent').where('timestamp', '>=', todayStart).orderBy('timestamp') : null,
        clearingData: {},
        transform: snapshot => ({ views: snapshot.size, dateKey: dateToLocalIsoString(todayStart) })
      })
    },
    setCurrentBusinessViews({ commit, state }, { views, dateKey }) {
      commit('setCurrentBusinessViews', { views: views, dateKey })
    },
    setReferredPartnerViews({ commit, state }, { partnershipId, partnerViews }) {
      commit('setReferredPartnerViews', { partnershipId, partnerViews })
    },
    setReferredPartnerPurchasees({ commit, state }, { partnershipId, totalCount, totalCommission }) {
      commit('setReferredPartnerPurchasees', { partnershipId, totalCount, totalCommission })
    }
  },
  strict: debug,
  plugins: debug ? [createLogger()] : []
})

export default store



// WEBPACK FOOTER //
// ./src/store.js